import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import {
  formatSharedMailboxLines,
  invalidSharedMailboxLines,
  M365_SEARCH_ALL_SOURCES,
  type M365SearchAllConfig,
  normalizeM365SearchAllConfig,
  normalizeM365SharedMailboxes,
  parseSharedMailboxLines,
  withM365SearchAllConfig,
  withM365SharedMailboxes,
} from "../utils/microsoft-365-search-config"
import { KeyValueOverrides } from "./shared/key-value-overrides"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditToolDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  agentName: string
  toolName: string | null
}

type StoredTool = {
  enabled?: boolean
  display_name?: string
  description?: string
  config?: Record<string, unknown>
  access?: Record<string, unknown>
  [key: string]: unknown
}

/**
 * Edit dialog for a per-agent tool override.
 *
 * Tools are layered: the base tool definition (name, description, schema)
 * lives in the repo at ``agent_runtime/tools/<tool>/schema.json`` — that
 * isn't editable from the admin UI. What IS editable is the per-agent
 * override dict stored at ``system_tools[agent_name][tool_name]`` in
 * Cosmos: enable/disable flag, display-name / description overrides,
 * tool-specific config, and access policy.
 *
 * The Form tab surfaces the commonly-edited fields; the Raw tab shows
 * the full stored override so admins can add forward-compatible keys
 * without waiting for a new form field to ship.
 */
export function EditToolDialog({ isOpen, onOpenChange, agentName, toolName }: EditToolDialogProps) {
  const { t } = useTranslation()
  const endpoint =
    agentName && toolName
      ? `/admin/agents/${encodeURIComponent(agentName)}/tools/${encodeURIComponent(toolName)}/raw`
      : null

  const {
    data: rawData,
    isLoading,
    refetch,
  } = useRawEntityQuery<StoredTool>(endpoint, isOpen && !!endpoint)

  const saveMutation = useSaveRawEntityMutation<StoredTool>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [["admin", "agents", agentName, "tools"]],
  })

  const stored: StoredTool = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""

  const [enabled, setEnabled] = useState(true)
  const [displayName, setDisplayName] = useState("")
  const [description, setDescription] = useState("")
  const [config, setConfig] = useState<Record<string, unknown>>({})
  // Draft text of the shared-mailbox editor. Kept separate from `config` so a
  // half-typed address is not dropped mid-keystroke; `config.shared_mailboxes`
  // always holds the parsed, valid rows.
  const [sharedMailboxText, setSharedMailboxText] = useState("")
  const [resetSignal, setResetSignal] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const isMicrosoft365SearchTool = toolName === "microsoft_365_search"

  useEffect(() => {
    // Default to enabled when the override doc has no explicit `enabled`
    // key — otherwise opening the dialog on a tool that has no override
    // yet and saving any other field would silently disable a tool that
    // was on by default.
    setEnabled(stored.enabled ?? true)
    setDisplayName(typeof stored.display_name === "string" ? stored.display_name : "")
    setDescription(typeof stored.description === "string" ? stored.description : "")
    const storedConfig =
      stored.config && typeof stored.config === "object" ? { ...stored.config } : {}
    setConfig(storedConfig)
    setSharedMailboxText(formatSharedMailboxLines(normalizeM365SharedMailboxes(storedConfig)))
    setResetSignal((n) => n + 1)
  }, [stored])

  useEffect(() => {
    if (!isOpen) setFormError(null)
  }, [isOpen])

  const searchAllConfig = useMemo(() => normalizeM365SearchAllConfig(config), [config])
  const sharedMailboxes = useMemo(() => normalizeM365SharedMailboxes(config), [config])
  const ignoredSharedMailboxLines = useMemo(
    () => invalidSharedMailboxLines(sharedMailboxText),
    [sharedMailboxText]
  )
  const genericConfig = useMemo(() => {
    if (!isMicrosoft365SearchTool) return config
    const { search_all: _searchAll, shared_mailboxes: _sharedMailboxes, ...rest } = config
    return rest
  }, [config, isMicrosoft365SearchTool])

  const setSearchAllConfig = (nextSearchAll: M365SearchAllConfig) => {
    const normalized = normalizeM365SearchAllConfig({ search_all: nextSearchAll })
    setConfig((current) => withM365SearchAllConfig(current, normalized))
  }

  const handleSharedMailboxTextChange = (value: string) => {
    setSharedMailboxText(value)
    const parsed = parseSharedMailboxLines(value)
    setConfig((current) => withM365SharedMailboxes(current, parsed))
  }

  const handleGenericConfigChange = (nextGenericConfig: Record<string, unknown>) => {
    if (!isMicrosoft365SearchTool) {
      setConfig(nextGenericConfig)
      return
    }
    // The dedicated M365 editors own these keys; keep them across generic edits.
    const preserved: Record<string, unknown> = {}
    if ("search_all" in config) preserved.search_all = config.search_all
    if ("shared_mailboxes" in config) preserved.shared_mailboxes = config.shared_mailboxes
    setConfig({ ...nextGenericConfig, ...preserved })
  }

  const toggleSearchAllSource = (
    sourceId: M365SearchAllConfig["sources"][number],
    checked: boolean
  ) => {
    const currentSources = searchAllConfig.sources
    const nextSources = checked
      ? [...currentSources, sourceId].filter(
          (source, index, sources) => sources.indexOf(source) === index
        )
      : currentSources.filter((source) => source !== sourceId)

    if (nextSources.length === 0) return
    setSearchAllConfig({ ...searchAllConfig, sources: nextSources })
  }

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!endpoint || !hash || !toolName) return
    setFormError(null)

    const next: StoredTool = {
      ...stored,
      enabled,
    }
    // Only persist display_name / description if non-empty; an empty string
    // means "don't override — use base tool's value".
    if (displayName.trim()) next.display_name = displayName.trim()
    else delete next.display_name
    if (description.trim()) next.description = description.trim()
    else delete next.description
    next.config = config

    try {
      await saveMutation.mutateAsync({
        entity: next as Record<string, unknown>,
        expectedHash: hash,
        reason: t("adminDashboard.toolEdit.reasonForm", {
          defaultValue: "Form-tab edit of tool {{tool}} (agent {{agent}})",
          agent: agentName,
          tool: toolName,
        }),
        source: "form",
      })
      void refetch()
      toast.success(t("adminDashboard.toolEdit.toastSaved", { defaultValue: "Tool updated" }))
      onOpenChange(false)
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.toolEdit.errorSaveFailed", { defaultValue: "Save failed" })
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!endpoint || !hash || !toolName) return
    try {
      await saveMutation.mutateAsync({
        entity: (next || {}) as Record<string, unknown>,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.toolEdit.toastSavedRaw", { defaultValue: "Tool saved (raw)" })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.toolEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!toolName) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.toolEdit.title", { defaultValue: "Edit tool" })}{" "}
            <code className="font-mono text-sm">{toolName}</code>{" "}
            <span className="text-muted-foreground text-sm font-normal">({agentName})</span>
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.toolEdit.description", {
              defaultValue:
                "Override the tool's display name, description, and config for this agent. Leave display name and description empty to use the base tool values. The Raw tab shows the full stored override document.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.toolEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.toolEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4 max-h-[60vh] overflow-y-auto" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="tool-enabled">
                    {t("adminDashboard.toolEdit.fieldEnabled", { defaultValue: "Enabled" })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.toolEdit.fieldEnabledHelp", {
                      defaultValue:
                        "Disabled tools stay in the agent's catalog but can't be invoked.",
                    })}
                  </p>
                </div>
                <Switch checked={enabled} id="tool-enabled" onCheckedChange={setEnabled} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tool-display-name">
                  {t("adminDashboard.toolEdit.fieldDisplayName", { defaultValue: "Display name" })}
                </Label>
                <Input
                  id="tool-display-name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.toolEdit.fieldDisplayNameHelp", {
                    defaultValue: "Leave empty to fall back to the base tool's display name.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tool-description">
                  {t("adminDashboard.toolEdit.fieldDescription", { defaultValue: "Description" })}
                </Label>
                <Textarea
                  id="tool-description"
                  rows={4}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.toolEdit.fieldDescriptionHelp", {
                    defaultValue: "Leave empty to fall back to the base tool's description.",
                  })}
                </p>
              </div>

              {isMicrosoft365SearchTool ? (
                <div className="space-y-4 rounded-md border p-3">
                  <div className="space-y-1">
                    <Label>
                      {t("adminDashboard.toolEdit.m365SearchAllTitle", {
                        defaultValue: "Federated Microsoft 365 search",
                      })}
                    </Label>
                    <p className="text-muted-foreground text-xs">
                      {t("adminDashboard.toolEdit.m365SearchAllHelp", {
                        defaultValue:
                          "Controls the default sources and per-source limits used by search_all for this agent.",
                      })}
                    </p>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {M365_SEARCH_ALL_SOURCES.map((source) => (
                      <label
                        key={source.id}
                        className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                      >
                        <Checkbox
                          checked={searchAllConfig.sources.includes(source.id)}
                          disabled={saveMutation.isPending}
                          onCheckedChange={(checked) =>
                            toggleSearchAllSource(source.id, checked === true)
                          }
                        />
                        <span>{source.label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="m365-search-all-top">
                        {t("adminDashboard.toolEdit.m365TopPerSource", {
                          defaultValue: "Results per source",
                        })}
                      </Label>
                      <Input
                        id="m365-search-all-top"
                        max={25}
                        min={1}
                        type="number"
                        value={searchAllConfig.top_per_source}
                        onChange={(event) => {
                          const parsed = Number.parseInt(event.target.value, 10)
                          setSearchAllConfig({
                            ...searchAllConfig,
                            top_per_source: Number.isFinite(parsed) ? parsed : 10,
                          })
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="m365-search-all-timeout">
                        {t("adminDashboard.toolEdit.m365SourceTimeout", {
                          defaultValue: "Source timeout seconds",
                        })}
                      </Label>
                      <Input
                        id="m365-search-all-timeout"
                        max={30}
                        min={1}
                        step="0.5"
                        type="number"
                        value={searchAllConfig.source_timeout_seconds}
                        onChange={(event) => {
                          const parsed = Number.parseFloat(event.target.value)
                          setSearchAllConfig({
                            ...searchAllConfig,
                            source_timeout_seconds: Number.isFinite(parsed) ? parsed : 12,
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {isMicrosoft365SearchTool ? (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="space-y-1">
                    <Label htmlFor="m365-shared-mailboxes">
                      {t("adminDashboard.toolEdit.m365SharedMailboxesTitle", {
                        defaultValue: "Shared mailboxes",
                      })}
                    </Label>
                    <p className="text-muted-foreground text-xs">
                      {t("adminDashboard.toolEdit.m365SharedMailboxesHelp", {
                        defaultValue:
                          "Shared mailboxes Hello AI may be pointed at, one per line as address | description (e.g. support@company.com | Customer support). Listing a mailbox does not grant access: Hello AI reads it with the signed-in user's own permissions, so users only ever see mail they can already open in Outlook.",
                      })}
                    </p>
                  </div>
                  <Textarea
                    disabled={saveMutation.isPending}
                    id="m365-shared-mailboxes"
                    rows={4}
                    value={sharedMailboxText}
                    placeholder={
                      "support@company.com | Customer support\norders@company.com | Order inbox"
                    }
                    onChange={(event) => handleSharedMailboxTextChange(event.target.value)}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.toolEdit.m365SharedMailboxesCount", {
                      defaultValue: "{{count}} shared mailbox(es) enabled.",
                      count: sharedMailboxes.length,
                    })}
                    {ignoredSharedMailboxLines.length > 0
                      ? ` ${t("adminDashboard.toolEdit.m365SharedMailboxesIgnored", {
                          defaultValue: "Ignored (not a valid address): {{lines}}",
                          lines: ignoredSharedMailboxLines.join(", "),
                        })}`
                      : null}
                  </p>
                </div>
              ) : null}

              <KeyValueOverrides
                disabled={saveMutation.isPending}
                value={genericConfig}
                description={t("adminDashboard.toolEdit.configDescription", {
                  defaultValue:
                    "Tool-specific config passed to the tool implementation. Values are strings unless you use Raw mode.",
                })}
                label={t("adminDashboard.toolEdit.configLabel", {
                  defaultValue: "Config overrides",
                })}
                onChange={handleGenericConfigChange}
              />

              {formError ? <p className="text-destructive text-xs">{formError}</p> : null}

              <DialogFooter>
                <Button
                  disabled={saveMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.toolEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={saveMutation.isPending || isLoading || !hash} type="submit">
                  {saveMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.toolEdit.saving", { defaultValue: "Saving…" })}
                    </>
                  ) : (
                    t("adminDashboard.toolEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={toolName}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.toolEdit.rawResourceLabel", {
                defaultValue: "tool {{tool}} (agent {{agent}})",
                agent: agentName,
                tool: toolName,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
